import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import './NotificationTierSelector.scss';
import { GET_NOTIFICATION_TIERS_FOR_PLAN_GQL } from '../../pages/OrgManagementPage/queries';
import { NO_NOTIFICATIONS_TIER_NAME } from '../../utils/constants';

function NotificationTierSelector({
  selectedTierId = '',
  className = '',
  onTierChange = () => { },
  disabled = false,
  label = 'Notification Tier',
  showDescription = true,
  planId = null,
  tiers: tiersProp = null,
  portalTarget = null,
  defaultText = null,
  defaultTierId = null,
  defaultLabel = '(My Default)',
}) {
  // --- State hooks ---
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // --- Apollo/GraphQL hooks ---
  const {
    loading,
    error,
    data: tiersData,
  } = useQuery(GET_NOTIFICATION_TIERS_FOR_PLAN_GQL, {
    variables: {
      plan_id: planId,
    },
    skip: !planId || Array.isArray(tiersProp),
    fetchPolicy: 'cache-first',
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });

  // --- Navigation/side effects ---
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // --- Derived state ---
  const tiers = tiersProp ?? tiersData?.notification_tier ?? [];

  // Find the "No Notifications" tier if it exists
  const noNotificationsTier = tiers.find((tier) => tier.name === NO_NOTIFICATIONS_TIER_NAME);

  // Display explicit selection, else inherited default (org tier), else plan default
  const effectiveSelectedTierId = selectedTierId ?? defaultTierId ?? noNotificationsTier?.id ?? null;
  const isInheritedDefault = !selectedTierId && defaultTierId && effectiveSelectedTierId === defaultTierId;

  const selectedTier = tiers.find((tier) => tier.id === effectiveSelectedTierId);

  // --- Function definitions ---
  const handleTierSelect = (tierId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (!tierId && noNotificationsTier) {
      onTierChange(noNotificationsTier.id);
    } else {
      onTierChange(tierId || null);
    }
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  if (!tiersProp && loading) {
    return (
      <div className="NotificationTierSelector">
        <LoadingSkeleton style={{ width: '100%', height: '40px' }} />
      </div>
    );
  }

  if (!tiersProp && error) {
    return (
      <div className={`NotificationTierSelector ${className}`}>
        <div className="NotificationTierSelector-error">Error loading tiers</div>
      </div>
    );
  }

  const selectedTierName = selectedTier?.name || defaultText || 'Select a Tier';

  return (
    <div className={`NotificationTierSelector ${className}`}>
      {label && <label className="NotificationTierSelector-label">{label}</label>}
      <div
        ref={buttonRef}
        className={`NotificationTierSelector-button ${disabled ? 'NotificationTierSelector-button--disabled' : ''} ${isOpen ? 'NotificationTierSelector-button--open' : ''
          }`}
        onClick={toggleDropdown}
      >
        <span className="NotificationTierSelector-buttonText">
          <b>{selectedTierName}</b>{' '}
          {(defaultTierId && defaultTierId === selectedTierId) || isInheritedDefault ? defaultLabel : ''}
        </span>
        <span className="NotificationTierSelector-chevron">
          {isOpen ? <>&#8593;</> : <>&#8595;</>}
        </span>
      </div>
      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <ul
            ref={dropdownRef}
            className="NotificationTierSelector-list"
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {tiers.map((tier) => {
              const isSelected = tier.id === effectiveSelectedTierId;
              const isDefault = defaultTierId && tier.id === defaultTierId;
              return (
                <li
                  key={tier.id}
                  className={`NotificationTierSelector-listItem ${isSelected ? 'NotificationTierSelector-listItem--selected' : ''
                    }`}
                  onClick={(e) => handleTierSelect(tier.id, e)}
                >
                  <div className="NotificationTierSelector-listItemContent">
                    <div className="NotificationTierSelector-listItemText">
                      <div className="NotificationTierSelector-listItemName">
                        {tier.name}
                        {isDefault && <span className="NotificationTierSelector-listItemDefault"> {defaultLabel}</span>}
                      </div>
                      {tier.description && (
                        <div className="NotificationTierSelector-listItemDescription">{tier.description}</div>
                      )}
                    </div>
                    {isSelected && (
                      <div className="NotificationTierSelector-listItemCheck">
                        <span>✓</span>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>,
          portalTarget || document.body,
        )}
      {showDescription && selectedTier && (
        <div className="NotificationTierSelector-description">{selectedTier.public_description}</div>
      )}
    </div>
  );
}

NotificationTierSelector.propTypes = {
  className: PropTypes.string,
  selectedTierId: PropTypes.string,
  onTierChange: PropTypes.func,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  showDescription: PropTypes.bool,
  planId: PropTypes.string,
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      sort_order: PropTypes.number,
      editable: PropTypes.bool,
      public_description: PropTypes.string,
    }),
  ),
  portalTarget: PropTypes.object,
  defaultText: PropTypes.string,
  defaultTierId: PropTypes.string,
  defaultLabel: PropTypes.string,
  props: PropTypes.object,
};

export default NotificationTierSelector;
